import React, { useContext, useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  message,
  Select,
  Button,
  Table,
  Space,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  TaskTemplate,
  TaskTemplateItem,
} from "../../../../../types/taskTemplate";
import { TaskTemplateOperation } from "../../../../../serviceoperation/TaskTemplateOperation";
import { CompanyContext } from "../../../../../contexts/company-context";
import { BranchOperation } from "../../../../../serviceoperation/BranchOperation";
import TaskTemplateItemModal from "./TaskTemplateItemModal";

interface TaskTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (template: Partial<TaskTemplate>) => void;
  template?: TaskTemplate | null;
  mode: "create" | "edit";
}

const TaskTemplateModal: React.FC<TaskTemplateModalProps> = ({
  open,
  onClose,
  onSave,
  template,
  mode,
}) => {
  const [form] = Form.useForm();
  const { selectedCompany } = useContext(CompanyContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [branches, setBranches] = useState<{ value: number; label: string }[]>(
    [],
  );
  const [items, setItems] = useState<TaskTemplateItem[]>([]);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TaskTemplateItem | null>(null);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);

  const tempOps = new TaskTemplateOperation();
  const branchOps = new BranchOperation();

  useEffect(() => {
    if (selectedCompany) {
      branchOps.getBranchAsync(selectedCompany.corporationId).then((value) => {
        if (value.data) {
          setBranches(
            value.data.map((branch) => ({
              value: branch.branchID,
              label: branch.name,
            })),
          );
        }
      });
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && template) {
        form.setFieldsValue({
          name: template.name,
          description: template.description,
          category: template.category,
          branchId: template.branchId,
        });
        setItems(template.items || []);
      } else {
        form.resetFields();
        setItems([]);
      }
    }
  }, [open, mode, template, form]);

  const handleAddItem = () => {
    setEditingItem(null);
    setEditingItemIndex(null);
    setIsItemModalOpen(true);
  };

  const handleEditItem = (item: TaskTemplateItem, index: number) => {
    setEditingItem(item);
    setEditingItemIndex(index);
    setIsItemModalOpen(true);
  };

  const handleDeleteItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSaveItem = (item: TaskTemplateItem) => {
    if (editingItemIndex !== null) {
      // Edit existing item
      const newItems = [...items];
      newItems[editingItemIndex] = item;
      setItems(newItems);
    } else {
      // Add new item
      setItems([...items, item]);
    }
    setIsItemModalOpen(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (items.length === 0) {
        message.error("Please add at least one task item");
        setLoading(false);
        return;
      }

      const templateData: Partial<TaskTemplate> = {
        name: values.name,
        description: values.description,
        category: values.category,
        branchId: values.branchId,
        items: items,
      };

      if (mode === "edit" && template) {
        templateData.id = template.id;
        const value = await tempOps.putTaskTemplateAsync(
          selectedCompany?.corporationId as number,
          template.id,
          {
            name: values.name,
            description: values.description,
            category: values.category,
            branchId: values.branchId,
            items: items,
          },
        );

        if (value.statusCode >= 200 && value.statusCode < 300) {
          onSave(templateData);
          onClose();
          form.resetFields();
          setItems([]);
          message.success(`Task template updated successfully!`);
        } else {
          message.error(
            value.statusMessage ||
              "An error has occurred while processing your request!",
          );
        }
      } else {
        const value = await tempOps.postTaskTemplateAsync(
          selectedCompany?.corporationId as number,
          {
            name: values.name,
            description: values.description,
            category: values.category,
            branchId: values.branchId,
            items: items,
          },
        );

        if (value.statusCode === 201) {
          const createdTemplateId =
            value.data?.taskTemplateId ||
            (value.data as unknown as { templateId?: string; id?: string })
              ?.templateId ||
            (value.data as unknown as { templateId?: string; id?: string })
              ?.id ||
            (
              value as unknown as {
                taskTemplateId?: string;
                templateId?: string;
                id?: string;
              }
            ).taskTemplateId ||
            (
              value as unknown as {
                taskTemplateId?: string;
                templateId?: string;
                id?: string;
              }
            ).templateId ||
            (
              value as unknown as {
                taskTemplateId?: string;
                templateId?: string;
                id?: string;
              }
            ).id;
          templateData.id = createdTemplateId;
          onSave(templateData);
          onClose();
          form.resetFields();
          setItems([]);
          message.success(`Task template created successfully!`);
        } else {
          message.error(
            value.statusMessage ||
              "An error has occurred while processing your request!",
          );
        }
      }
    } catch (error) {
      message.error("An error has occurred while processing your request!");
      console.error("Task template submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
    },
    {
      title: "Est. Minutes",
      dataIndex: "estimatedMinutes",
      key: "estimatedMinutes",
      width: 120,
    },
    {
      title: "Photo",
      dataIndex: "requirePhoto",
      key: "requirePhoto",
      width: 80,
      render: (value: boolean) => (value ? "Yes" : "No"),
    },
    {
      title: "Note",
      dataIndex: "requireNote",
      key: "requireNote",
      width: 80,
      render: (value: boolean) => (value ? "Yes" : "No"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: unknown, record: TaskTemplateItem, index: number) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditItem(record, index)}
          />
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteItem(index)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          mode === "create" ? "Create Task Template" : "Edit Task Template"
        }
        open={open}
        onOk={handleSubmit}
        onCancel={() => {
          onClose();
          form.resetFields();
          setItems([]);
        }}
        confirmLoading={loading}
        okText={mode === "create" ? "Create" : "Save"}
        width={900}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 24 }}
          requiredMark="optional"
        >
          <Form.Item
            name="name"
            label="Template Name"
            rules={[
              { required: true, message: "Please enter template name" },
              { max: 150, message: "Name cannot exceed 150 characters" },
            ]}
          >
            <Input placeholder="e.g., Daily Tasks, Weekly Review" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { max: 500, message: "Description cannot exceed 500 characters" },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Describe the purpose and tasks for this template..."
            />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[
              { max: 50, message: "Category cannot exceed 50 characters" },
            ]}
          >
            <Input placeholder="e.g., Maintenance, Inspection, Cleaning" />
          </Form.Item>

          <Form.Item name="branchId" label="Branch (Optional)">
            <Select
              placeholder="Select Branch"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={branches}
            />
          </Form.Item>

          <Form.Item label="Task Items">
            <div style={{ marginBottom: 8 }}>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddItem}
                block
              >
                Add Task Item
              </Button>
            </div>
            <Table
              dataSource={items.map((item, index) => ({ ...item, key: index }))}
              columns={columns}
              size="small"
              pagination={false}
              locale={{ emptyText: "No task items added yet" }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <TaskTemplateItemModal
        open={isItemModalOpen}
        onClose={() => setIsItemModalOpen(false)}
        onSave={handleSaveItem}
        item={editingItem}
        mode={editingItem ? "edit" : "create"}
      />
    </>
  );
};

export default TaskTemplateModal;
